import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, ChevronDown, Loader2, TrendingUp, Info } from "lucide-react";
import api from "@/lib/api";
import { monthKey, monthLabel, fmtEuro, fmtPct, fmtHours } from "@/lib/dates";

const COLORS = {
  green: { dot: "bg-emerald-500", text: "text-emerald-600", chip: "bg-emerald-50 text-emerald-700 border-emerald-200", label: "Buon guadagno" },
  yellow: { dot: "bg-amber-500", text: "text-amber-600", chip: "bg-amber-50 text-amber-700 border-amber-200", label: "Poco guadagno" },
  red: { dot: "bg-red-500", text: "text-red-600", chip: "bg-red-50 text-red-700 border-red-200", label: "In perdita" },
  neutral: { dot: "bg-slate-300", text: "text-slate-500", chip: "bg-slate-50 text-slate-500 border-slate-200", label: "Nessun dato" },
};

export default function AdminProfitability() {
  const [cursor, setCursor] = useState(new Date());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(null);

  const month = monthKey(cursor);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/reports/profitability?month=${month}`);
      setData(res.data);
      setExpanded(null);
    } catch {
      toast.error("Errore nel caricamento della redditività");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [month]);

  const move = (delta) => {
    const d = new Date(cursor);
    d.setMonth(d.getMonth() + delta);
    setCursor(d);
  };

  const totals = data?.totals;
  const tc = COLORS[totals?.color] || COLORS.neutral;

  return (
    <div className="space-y-4">
      <h1 className="font-head text-2xl font-bold text-slate-900 flex items-center gap-2">
        <TrendingUp className="h-6 w-6 text-primary" /> Redditività
      </h1>

      {/* Month nav */}
      <div className="flex items-center justify-between bg-white border border-slate-200 rounded-xl p-2">
        <button data-testid="profit-prev-month" onClick={() => move(-1)} className="h-11 w-11 rounded-lg border border-slate-200 flex items-center justify-center active:scale-95">
          <ChevronLeft className="h-6 w-6" />
        </button>
        <span data-testid="profit-month" className="font-head font-bold text-lg">{monthLabel(cursor)}</span>
        <button data-testid="profit-next-month" onClick={() => move(1)} className="h-11 w-11 rounded-lg border border-slate-200 flex items-center justify-center active:scale-95">
          <ChevronRight className="h-6 w-6" />
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="h-7 w-7 animate-spin text-primary" /></div>
      ) : !data ? null : (
        <>
          {/* Totals summary */}
          <div data-testid="profit-summary" className="bg-white border border-slate-200 rounded-2xl p-4 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm font-semibold uppercase tracking-wide text-slate-400">Totale mese</span>
              <span className={`text-xs font-bold px-2.5 py-1 rounded-full border ${tc.chip}`}>{fmtPct(totals.margin_pct)}</span>
            </div>
            <div className="grid grid-cols-3 gap-2 text-center">
              <div>
                <p className="text-xs text-slate-400">Ricavi</p>
                <p className="font-head font-bold text-slate-900" data-testid="total-revenue">{fmtEuro(totals.revenue)}</p>
              </div>
              <div>
                <p className="text-xs text-slate-400">Costi</p>
                <p className="font-head font-bold text-slate-900" data-testid="total-cost">{fmtEuro(totals.cost)}</p>
              </div>
              <div>
                <p className="text-xs text-slate-400">Margine</p>
                <p className={`font-head font-extrabold ${tc.text}`} data-testid="total-margin">{fmtEuro(totals.margin)}</p>
              </div>
            </div>
          </div>

          {/* Jobs list */}
          {data.jobs.length === 0 ? (
            <p className="text-slate-400 text-sm">Nessun lavoro o ora registrata per questo mese.</p>
          ) : (
            <div className="space-y-2">
              {data.jobs.map((j) => {
                const c = COLORS[j.color] || COLORS.neutral;
                const open = expanded === j.job_id;
                return (
                  <div key={j.job_id} data-testid="profit-job-card" className="bg-white border border-slate-200 rounded-xl overflow-hidden">
                    <button data-testid={`profit-toggle-${j.job_id}`} onClick={() => setExpanded(open ? null : j.job_id)} className="w-full p-4 flex items-start justify-between gap-2 text-left active:bg-slate-50">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className={`h-3 w-3 rounded-full shrink-0 ${c.dot}`} data-testid={`profit-dot-${j.job_id}`} />
                          <span className="font-semibold text-slate-900 text-lg truncate">{j.name}</span>
                          {!j.active && <span className="text-xs text-slate-400">(elim.)</span>}
                        </div>
                        <div className="flex gap-3 mt-1 text-sm text-slate-500 pl-5">
                          <span>Ric. <span className="font-semibold text-slate-700">{fmtEuro(j.revenue)}</span></span>
                          <span>Cost. <span className="font-semibold text-slate-700">{fmtEuro(j.cost)}</span></span>
                        </div>
                      </div>
                      <div className="text-right shrink-0 flex flex-col items-end gap-0.5">
                        <span className={`font-head font-extrabold ${c.text}`} data-testid={`profit-margin-${j.job_id}`}>{fmtEuro(j.margin)}</span>
                        <span className={`text-xs font-bold px-2 py-0.5 rounded-full border ${c.chip}`}>{fmtPct(j.margin_pct)}</span>
                      </div>
                    </button>

                    {open && (
                      <div data-testid={`profit-detail-${j.job_id}`} className="px-4 pb-4 border-t border-slate-100 pt-3 space-y-2">
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-slate-500">Ore totali · prezzo</span>
                          <span className="font-semibold text-slate-700">{fmtHours(j.hours)} h × {fmtEuro(j.hourly_rate)}</span>
                        </div>
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-slate-500">Ricavo</span>
                          <span className="font-semibold text-emerald-600">{fmtEuro(j.revenue)}</span>
                        </div>

                        <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 pt-1">Costo dipendenti</p>
                        {j.employees.length === 0 ? (
                          <p className="text-sm text-slate-400">Nessuna ora registrata.</p>
                        ) : (
                          j.employees.map((e) => (
                            <div key={e.employee_id} className="flex items-center justify-between text-sm">
                              <span className="text-slate-600 min-w-0 truncate">{e.name} <span className="text-slate-400">({fmtHours(e.hours)}h × {fmtEuro(e.real_hourly_cost)})</span></span>
                              <span className="font-semibold text-slate-700 ml-2 whitespace-nowrap">{fmtEuro(e.cost)}</span>
                            </div>
                          ))
                        )}
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-slate-600">Quota ammortamento</span>
                          <span className="font-semibold text-slate-700">{fmtEuro(j.amortization)}</span>
                        </div>
                        <div className="flex items-center justify-between text-sm border-t border-slate-100 pt-2">
                          <span className="text-slate-500">Costo totale</span>
                          <span className="font-semibold text-red-600">{fmtEuro(j.cost)}</span>
                        </div>
                        <div className="flex items-center justify-between border-t border-slate-100 pt-2">
                          <span className="font-semibold text-slate-700">Margine</span>
                          <span className={`font-head font-extrabold ${c.text}`}>{fmtEuro(j.margin)} · {fmtPct(j.margin_pct)}</span>
                        </div>
                        <p className={`text-xs font-semibold ${c.text}`}>{c.label}</p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Info footer */}
          <div className="flex gap-2 text-xs text-slate-500 bg-slate-50 rounded-xl p-3">
            <Info className="h-4 w-4 shrink-0 mt-0.5" />
            <p>
              Costo reale dipendente = lordo × {Number(data.cost_multiplier).toLocaleString("it-IT", { maximumFractionDigits: 2 })}.
              Quota ammortamento per lavoro: {fmtEuro(data.amort_per_job)}/mese ({data.num_active_jobs} lavori attivi).
              Modifica moltiplicatore, soglie e ammortamenti in Impostazioni.
            </p>
          </div>
        </>
      )}
    </div>
  );
}
